import { useCallback, useMemo, useState } from 'react';
import { ShortlistItemProvider } from '@karehero/models';
import { DEFAULT_SORT } from '@karehero/llama/pwa/organisms/CareProviderResults/CareProviderResults';
import {
  useAdminSearchCareHomesQuery,
  useAdminSearchHomeCareQuery,
} from 'store/api/findCare';

type CareType = 'careHome' | 'homeCare';

const FUNDING_SLUG_MAP: Record<string, string[]> = {
  'Self-funded': ['self-funded-private'],
  'Local authority': ['local-authority', 'local-authority-with-top-up'],
  NHS: ['nhs-continuing-healthcare'],
};

const PAGE_SIZE = 20;

const DEFAULT_DISTANCE = '80km';

const DISTANCE_KM_MAP: Record<string, string> = {
  'Within 5 miles': '8km',
  'Within 10 miles': '16km',
  'Within 25 miles': '40km',
  'Within 50 miles': '80km',
};

const MAX_HOURLY_RATE_MAP: Record<string, number> = {
  'Up to £15': 15,
  '£15 - £20': 20,
  '£20 - £25': 25,
};

const VISIT_DURATION_MAP: Record<string, string> = {
  '60 minutes': '60',
  '45 minutes': '45',
  '30 minutes': '30',
  '15 minutes': '15',
};

const RATING_INDEX_MAP: Record<string, number> = {
  Outstanding: 5,
  Good: 4,
};

const SORT_MAP: Record<string, string> = {
  'Most relevant': 'relevance',
  Nearest: 'nearest',
  'Highest rating': 'highest_rating',
  'Lowest price': 'lowest_price',
  'Highest price': 'highest_price',
};

const buildCareHomeParams = (filters: Record<string, string>) => {
  const params: {
    distance?: string;
    min_rating_index?: number;
    service_types?: string[];
    dementia?: boolean;
    fundings?: string[];
  } = {};

  params.distance =
    (filters.distance && DISTANCE_KM_MAP[filters.distance]) || DEFAULT_DISTANCE;
  if (filters.rating && RATING_INDEX_MAP[filters.rating]) {
    params.min_rating_index = RATING_INDEX_MAP[filters.rating];
  }
  if (filters.careProvided) {
    if (filters.careProvided === 'Dementia') {
      params.dementia = true;
    } else {
      params.service_types = [filters.careProvided];
    }
  }
  if (filters.funding && FUNDING_SLUG_MAP[filters.funding]) {
    params.fundings = FUNDING_SLUG_MAP[filters.funding];
  }

  return params;
};

const buildHomeCareParams = (filters: Record<string, string>) => {
  const params: {
    distance?: string;
    min_rating_index?: number;
    max_hourly_rate?: number;
    visit_duration?: string;
  } = {};

  params.distance =
    (filters.distance && DISTANCE_KM_MAP[filters.distance]) || DEFAULT_DISTANCE;
  if (filters.rating && RATING_INDEX_MAP[filters.rating]) {
    params.min_rating_index = RATING_INDEX_MAP[filters.rating];
  }
  if (filters.hourlyRate && MAX_HOURLY_RATE_MAP[filters.hourlyRate]) {
    params.max_hourly_rate = MAX_HOURLY_RATE_MAP[filters.hourlyRate];
  }
  if (filters.visitDuration && VISIT_DURATION_MAP[filters.visitDuration]) {
    params.visit_duration = VISIT_DURATION_MAP[filters.visitDuration];
  }

  return params;
};

export const useProviderSearch = () => {
  const [searchLocation, setSearchLocation] = useState('');
  const [careType, setCareType] = useState<CareType>('careHome');
  // Matches the Distance filter the page starts with.
  const [filters, setFilters] = useState<Record<string, string>>({
    distance: 'Within 5 miles',
  });
  const [sort, setSort] = useState<string>(DEFAULT_SORT);

  // The page belongs to the search it was loaded for. A changed search starts
  // again from page 1, so going back to an earlier search can't bring its old
  // page back.
  const searchKey = JSON.stringify([searchLocation, careType, filters, sort]);
  const [pageState, setPageState] = useState({ key: searchKey, page: 1 });
  if (pageState.key !== searchKey) {
    setPageState({ key: searchKey, page: 1 });
  }
  const page = pageState.key === searchKey ? pageState.page : 1;

  const sortParam = SORT_MAP[sort];

  const careHomeParams = useMemo(
    () => ({
      location: searchLocation,
      per_page: PAGE_SIZE,
      page,
      sort: sortParam,
      ...buildCareHomeParams(filters),
    }),
    [searchLocation, filters, sortParam, page],
  );

  const homeCareParams = useMemo(
    () => ({
      location: searchLocation,
      per_page: PAGE_SIZE,
      page,
      sort: sortParam,
      ...buildHomeCareParams(filters),
    }),
    [searchLocation, filters, sortParam, page],
  );

  const careHomeQuery = useAdminSearchCareHomesQuery(careHomeParams, {
    skip: !searchLocation || careType !== 'careHome',
  });

  const homeCareQuery = useAdminSearchHomeCareQuery(homeCareParams, {
    skip: !searchLocation || careType !== 'homeCare',
  });

  const activeQuery = careType === 'homeCare' ? homeCareQuery : careHomeQuery;

  const results: ShortlistItemProvider[] = useMemo(
    () => activeQuery.data?.items ?? [],
    [activeQuery.data?.items],
  );

  const search = useCallback((location: string) => {
    setSearchLocation(location.trim());
  }, []);

  const loadMore = () => {
    // After a failed page, ask for that page again. Asking for the next one
    // would skip the failed page.
    if (activeQuery.isError) {
      activeQuery.refetch();
      return;
    }
    const loadedPage = activeQuery.data?.pagination.page ?? page;
    setPageState({ key: searchKey, page: loadedPage + 1 });
  };

  return {
    results,
    // Only the first page replaces the list with a spinner; later pages add to it.
    isLoading: activeQuery.isFetching && page === 1,
    isLoadingMore: activeQuery.isFetching && page > 1,
    hasMore: activeQuery.data?.hasMoreResults ?? false,
    loadMore,
    search,
    setCareType,
    setFilters,
    setSort,
    searchGeoPoint:
      (
        activeQuery.data as {
          searchLocation?: { lat: number; lon: number } | null;
        }
      )?.searchLocation ?? null,
  };
};
